import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { coaService } from '../../services/coaService';
import { glService } from '../../services/glService';
import { useClient } from '../../context/ClientContext';
import { formatCurrency } from '../../utils/formatters';

const { FiX, FiCalendar, FiArrowRight, FiTrash2 } = FiIcons;

export default function JournalEntryModal({ isOpen, onClose, onSuccess }) {
  const { activeClient } = useClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coaAccounts, setCoaAccounts] = useState([]);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    lines: [
      { description: '', coa_id: '', debit: '', credit: '' },
      { description: '', coa_id: '', debit: '', credit: '' }
    ]
  });

  useEffect(() => {
    if (isOpen && activeClient?.id) {
      loadCOA();
    }
  }, [isOpen, activeClient?.id]);

  const loadCOA = async () => {
    try {
      const data = await coaService.getChartOfAccounts(activeClient.id);
      setCoaAccounts(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load accounts from Chart of Accounts');
    }
  };

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { description: '', coa_id: '', debit: '', credit: '' }]
    }));
  };

  const removeLine = (index) => {
    if (formData.lines.length <= 2) return;
    const newLines = [...formData.lines];
    newLines.splice(index, 1);
    setFormData(prev => ({ ...prev, lines: newLines }));
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    
    // Logic: Debit and Credit are mutually exclusive on a single line
    if (field === 'debit' && value > 0) newLines[index].credit = '';
    if (field === 'credit' && value > 0) newLines[index].debit = '';
    
    setFormData(prev => ({ ...prev, lines: newLines }));
  };

  const totals = formData.lines.reduce((acc, line) => ({
    debit: acc.debit + Number(line.debit || 0),
    credit: acc.credit + Number(line.credit || 0)
  }), { debit: 0, credit: 0 });

  const difference = Math.abs(totals.debit - totals.credit);
  const isBalanced = difference < 0.01 && totals.debit > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isBalanced) return;

    setLoading(true);
    setError(null);

    try {
      await glService.createJournalEntry(activeClient.id, formData);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save journal entry');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Organize accounts by type for the dropdown
  const groupedAccounts = coaAccounts.reduce((acc, account) => {
    const type = account.type || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(account);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-8 py-5 flex items-center justify-between border-b border-slate-100">
          <div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">Journal Entry</h2>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-0.5">Manual Ledger Post</p>
          </div>
          <button onClick={onClose} className="p-2.5 text-slate-400 hover:text-slate-900 transition-all rounded-xl hover:bg-slate-100">
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-8">
          {error && (
            <div className="p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 text-rose-600 text-sm font-bold">
              <SafeIcon icon={FiIcons.FiAlertCircle} className="text-lg" />
              {error}
            </div>
          )}

          {/* Top Fields */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="md:col-span-1 space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Entry Date</label>
              <div className="relative">
                <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-xl transition-all font-bold text-slate-900 outline-none"
                />
              </div>
            </div>
            
            <div className="md:col-span-3 space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">General Memo</label>
              <input
                type="text"
                placeholder="Overall purpose of this journal entry..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-5 py-3.5 bg-slate-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-xl transition-all font-bold text-slate-900 outline-none"
              />
            </div>
          </div>

          {/* Lines Table */}
          <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-[35%]">Account</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Line Description</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right w-36">Debit</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right w-36">Credit</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {formData.lines.map((line, index) => (
                  <tr key={index} className="group hover:bg-slate-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <select
                        required
                        value={line.coa_id}
                        onChange={(e) => updateLine(index, 'coa_id', e.target.value)}
                        className="w-full px-4 py-3 bg-white border-2 border-slate-100 focus:border-blue-500 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all"
                      >
                        <option value="" disabled>Select Account...</option>
                        {Object.entries(groupedAccounts).map(([type, accounts]) => (
                          <optgroup key={type} label={type.toUpperCase()} className="text-[10px] font-black text-slate-400 bg-slate-50 uppercase">
                            {accounts.map(acc => (
                              <option key={acc.id} value={acc.id} className="text-sm font-bold text-slate-900 bg-white">
                                {acc.code ? `${acc.code} - ` : ''}{acc.name}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        placeholder="Memo for this line..."
                        value={line.description}
                        onChange={(e) => updateLine(index, 'description', e.target.value)}
                        className="w-full px-4 py-3 bg-white border-2 border-slate-100 focus:border-blue-500 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all italic placeholder:not-italic"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={line.debit}
                        onChange={(e) => updateLine(index, 'debit', e.target.value)}
                        className="w-full px-4 py-3 bg-white border-2 border-slate-100 focus:border-blue-500 rounded-xl text-sm font-black text-slate-900 text-right outline-none transition-all"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={line.credit}
                        onChange={(e) => updateLine(index, 'credit', e.target.value)}
                        className="w-full px-4 py-3 bg-white border-2 border-slate-100 focus:border-blue-500 rounded-xl text-sm font-black text-slate-900 text-right outline-none transition-all"
                      />
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        disabled={formData.lines.length <= 2}
                        className="p-2 text-slate-300 hover:text-rose-500 transition-all rounded-lg hover:bg-rose-50 disabled:opacity-0"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start gap-8">
            <button
              type="button"
              onClick={addLine}
              className="px-6 py-3 border-2 border-slate-100 text-slate-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center gap-2"
            >
              <SafeIcon icon={FiIcons.FiPlus} /> Add Line
            </button>

            <div className="bg-slate-900 rounded-[24px] p-6 w-full md:w-96 text-white shadow-xl">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Debits</p>
                  <p className="text-xl font-black mt-1">{formatCurrency(totals.debit)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Credits</p>
                  <p className="text-xl font-black mt-1">{formatCurrency(totals.credit)}</p>
                </div>
              </div>
              <div className="pt-5 border-t border-white/10 flex justify-between items-center">
                <p className="text-xs font-bold text-slate-400">Out of Balance</p>
                <p className={`text-xl font-black ${difference === 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {formatCurrency(difference)}
                </p>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-slate-100 flex justify-end gap-4 bg-slate-50/30">
          <button
            type="button"
            onClick={onClose}
            className="px-8 py-3.5 text-slate-500 font-black text-xs uppercase tracking-widest hover:bg-slate-100 rounded-xl transition-all"
          >
            Discard
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !isBalanced}
            className="px-12 py-3.5 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 disabled:opacity-50 disabled:grayscale transition-all shadow-lg shadow-blue-200"
          >
            {loading ? 'Posting...' : 'Post to Ledger'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}